"use client";

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  browserLocalPersistence,
  onAuthStateChanged,
  RecaptchaVerifier,
  setPersistence,
  signInWithPhoneNumber,
  signOut as firebaseSignOut,
  type ConfirmationResult,
  type User,
} from "firebase/auth";
import { getFirebaseAuth, isFirebaseConfigured } from "@/lib/firebase-client";

type FirebaseAuthContextValue = {
  /** The signed-in customer, or null when signed out. */
  user: User | null;
  /** True until the first auth-state resolution completes. */
  loading: boolean;
  /** False when the build has no Firebase config (membership disabled). */
  configured: boolean;
  /**
   * Sends an SMS OTP to the given E.164 phone number. Resolves with a
   * ConfirmationResult that callers pass to {@link confirmOtp}. The DOM node
   * with id `recaptchaContainerId` must be in the document — the invisible
   * reCAPTCHA verifier mounts there.
   */
  startPhoneVerification: (
    phoneE164: string,
    recaptchaContainerId: string,
  ) => Promise<ConfirmationResult>;
  /** Completes a phone sign-in by submitting the SMS code. */
  confirmOtp: (
    confirmation: ConfirmationResult,
    smsCode: string,
  ) => Promise<User>;
  signOut: () => Promise<void>;
  /** A fresh Firebase ID token for authenticating CMS calls, or null. */
  getIdToken: () => Promise<string | null>;
};

const FirebaseAuthContext = createContext<FirebaseAuthContextValue | null>(null);

export function FirebaseAuthProvider({ children }: { children: ReactNode }) {
  const configured = isFirebaseConfigured();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(configured);

  useEffect(() => {
    if (!configured) {
      return;
    }
    // Pin local persistence so the customer stays signed in across browser
    // restarts — Firebase Web defaults to this, but setting it explicitly
    // prevents a future regression.
    const auth = getFirebaseAuth();
    void setPersistence(auth, browserLocalPersistence).catch(() => {});
    return onAuthStateChanged(auth, (nextUser) => {
      setUser(nextUser);
      setLoading(false);
    });
  }, [configured]);

  const value = useMemo<FirebaseAuthContextValue>(
    () => ({
      user,
      loading,
      configured,
      startPhoneVerification: async (phoneE164, recaptchaContainerId) => {
        const auth = getFirebaseAuth();
        // RecaptchaVerifier must be re-created per send because the
        // underlying widget is single-use; the third arg is the auth instance
        // in the v9 modular SDK.
        const verifier = new RecaptchaVerifier(auth, recaptchaContainerId, {
          size: "invisible",
        });
        try {
          return await signInWithPhoneNumber(auth, phoneE164.trim(), verifier);
        } catch (error) {
          verifier.clear();
          throw error;
        }
      },
      confirmOtp: async (confirmation, smsCode) => {
        const result = await confirmation.confirm(smsCode.trim());
        return result.user;
      },
      signOut: async () => {
        await firebaseSignOut(getFirebaseAuth());
      },
      getIdToken: async () => (user ? user.getIdToken() : null),
    }),
    [user, loading, configured],
  );

  return (
    <FirebaseAuthContext.Provider value={value}>
      {children}
    </FirebaseAuthContext.Provider>
  );
}

export function useFirebaseAuth(): FirebaseAuthContextValue {
  const context = useContext(FirebaseAuthContext);
  if (!context) {
    throw new Error(
      "useFirebaseAuth must be used within a <FirebaseAuthProvider>.",
    );
  }
  return context;
}
